import appLogger from '@/utils/logger';
import { REQUEST_METHOD } from '@/constants';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { apiInstance } from '@/utils';
import { CsvUploadRequest } from '@/utils/csvParser';
import { CsvFieldMapping, validateCsvMapping } from '@/utils/csvAiMapping';
import { STORAGE_KEY } from '@/constants';
import { getClientContext } from '@/utils/clientContext';
import { WORKSPACE_STORAGE_KEY } from '@/constants/workspace';
import { getPublicApiBaseUrl } from '@/utils/publicApiBaseUrl';

const _queryWorkflow = async (data: { query: string }): Promise<{ data: any }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.queryWorkflow,
  });

  return result.data;
};

export interface CsvUploadResponse {
  message: string;
  list_id: number;
  insertedRows: {
    inserted: any[];
    skipped: any[];
  };
  summary: {
    totalRowsProvided: number;
    validRowsInserted: number;
    rowsSkipped: number;
  };
  skippedRows: any[];
}

export const uploadCsvData = async (csvData: CsvUploadRequest): Promise<CsvUploadResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: csvData,
    url: API_ENDPOINTS.uploadCsvForAudience,
  });

  return result.data;
};

/**
 * Get CSV field mapping from AI by sending sample data to /stream endpoint
 */
const getCsvFieldMapping = async (
  mappingPrompt: string,
  sessionId: string,
  previousError?: string,
  _attemptNumber: number = 1,
): Promise<CsvFieldMapping> => {
  const token = localStorage.getItem(STORAGE_KEY.ACCESS_TOKEN);

  if (!token) {
    throw new Error('No authentication token found');
  }

  // Add workspace header for private API calls
  const workspaceId = localStorage.getItem(WORKSPACE_STORAGE_KEY);
  const headers: any = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  if (workspaceId) {
    headers['x-workspace-id'] = workspaceId;
  }

  const response = await fetch(
    `${getPublicApiBaseUrl()}${API_ENDPOINTS.queryWorkflow}`,
    {
      method: REQUEST_METHOD.POST,
      headers,
      body: JSON.stringify({
        query: mappingPrompt,
        sessionId,
        clientContext: getClientContext(),
      }),
    },
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  let fullResponse = '';
  let buffer = '';
  let currentEvent = '';

  try {
    const decoder = new TextDecoder();
    const maxIterations = 1000; // Prevent infinite loops
    const timeout = 30000; // 30 second timeout
    const startTime = Date.now();
    let iterations = 0;
    let lastProgressTime = Date.now();

    while (iterations < maxIterations) {
      // Check timeout
      if (Date.now() - startTime > timeout) {
        throw new Error('CSV mapping request timed out after 30 seconds');
      }

      const { done, value } = await reader.read();
      if (done) break;

      iterations++;

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      // Update progress time when we receive data
      if (chunk.length > 0) {
        lastProgressTime = Date.now();
      }

      // Check for stalled streams (no data for 10 seconds)
      if (Date.now() - lastProgressTime > 10000) {
        throw new Error('CSV mapping stream stalled - no data received for 10 seconds');
      }

      if (buffer.includes('\n')) {
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines.filter((line) => line.trim() !== '')) {
          // Track current event type
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
            continue;
          }

          if (line.startsWith('data: ')) {
            const data = line.slice(6);

            // Skip control signals
            if (data === '[DONE]') break;

            // Skip empty lines and separators
            if (data === '' || data === '---') continue;

            // Handle both OpenAI format and assistant-content format
            try {
              // Try parsing as OpenAI chat completion format first
              const openAIData = JSON.parse(data);
              if (openAIData.choices && openAIData.choices[0]?.delta?.content) {
                fullResponse += openAIData.choices[0].delta.content;
                continue;
              }
            } catch {
              // Not OpenAI format, continue with other formats
            }

            // Handle assistant-content events for AI responses
            if (currentEvent === 'assistant-content') {
              try {
                // Parse the JSON data from assistant-content events
                const parsedData = JSON.parse(data);
                if (parsedData.type === 'content' && parsedData.content) {
                  fullResponse += parsedData.content;
                }
              } catch {
                // If parsing fails, treat as raw content
                appLogger.warn('Failed to parse assistant-content:', { data: data });
              }
            }
            // Skip session events (which contain the UUID)
            else if (currentEvent === 'session') {
              // Ignore session ID
              continue;
            }
            // Skip conversation-complete events
            else if (currentEvent === 'conversation-complete') {
              // End of conversation
              break;
            }
          }
        }
      }
    }

    // Check if we hit the iteration limit
    if (iterations >= maxIterations) {
      throw new Error('CSV mapping request exceeded maximum iterations (possible infinite loop)');
    }
  } finally {
    reader.releaseLock();
  }

  appLogger.info('Full AI response received:', { data: fullResponse });
  appLogger.info('Response length:', { data: fullResponse.length });

  // Clean up the response and extract JSON
  const cleanResponse = fullResponse.trim();

  // Check if we actually got content
  if (!cleanResponse) {
    appLogger.error('No content received from AI response');
    appLogger.error('Buffer state:', { data: buffer });
    appLogger.error('Current event:', { data: currentEvent });
    throw new Error(
      'No content received from AI. The AI must respond with a valid JSON object containing field mappings.',
    );
  }

  // Check if response is a UUID (in case parsing logic failed)
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidPattern.test(cleanResponse)) {
    appLogger.error('Only received session UUID, no actual AI content:', { data: cleanResponse });
    throw new Error(
      'Only received session ID, no AI content. Check if the AI model is responding properly.',
    );
  }

  // Check for common non-JSON responses
  if (cleanResponse.length < 10 || !cleanResponse.includes('field_mappings')) {
    appLogger.error('Response too short or missing field_mappings:', { data: cleanResponse });
    throw new Error(
      'AI response is too short or missing required field_mappings. Expected a complete JSON object.',
    );
  }

  // Find the start and end of the JSON object
  const jsonStart = cleanResponse.indexOf('{');
  const jsonEnd = cleanResponse.lastIndexOf('}');

  if (jsonStart === -1 || jsonEnd === -1 || jsonEnd <= jsonStart) {
    appLogger.error('No JSON object brackets found in response:', { data: cleanResponse });
    throw new Error(
      'No valid JSON object found. Response must start with { and end with }. Received: ' +
        cleanResponse.substring(0, 100),
    );
  }

  const jsonString = cleanResponse.substring(jsonStart, jsonEnd + 1);

  try {
    // First, try to parse the JSON
    const mapping = JSON.parse(jsonString);

    // Validate the structure
    if (!mapping || typeof mapping !== 'object') {
      throw new Error(
        'Parsed result is not a valid object. Expected JSON object with field mappings.',
      );
    }

    if (!mapping.detected_type) {
      throw new Error('Missing required field: detected_type. Must be "person" or "company".');
    }

    if (!mapping.field_mappings || typeof mapping.field_mappings !== 'object') {
      throw new Error(
        'Missing or invalid field_mappings. Must be an object with column name to field mappings.',
      );
    }

    // Check if field_mappings is empty
    if (Object.keys(mapping.field_mappings).length === 0) {
      throw new Error('field_mappings is empty. Must contain at least one column mapping.');
    }

    return validateCsvMapping(mapping);
  } catch (error) {
    // Log the problematic JSON for debugging
    appLogger.error('Failed to parse JSON:', { data: jsonString });
    appLogger.error('JSON parsing error:', { data: error });

    // Provide specific feedback for different error types
    if (error instanceof SyntaxError) {
      if (error.message.includes('Unexpected token')) {
        const match = error.message.match(/position (\d+)/);
        if (match) {
          const position = parseInt(match[1]);
          const snippet = jsonString.substring(
            Math.max(0, position - 20),
            Math.min(jsonString.length, position + 20),
          );
          throw new Error(
            `JSON syntax error at position ${position}. Context: "${snippet}". Check brackets, quotes, and commas.`,
          );
        }
        throw new Error(
          `JSON syntax error: ${error.message}. Ensure proper JSON formatting with correct brackets and quotes.`,
        );
      }
      if (error.message.includes('Unexpected end')) {
        throw new Error('JSON is incomplete. Make sure to close all brackets and quotes properly.');
      }
    }

    // Handle validation errors specifically
    if (error instanceof Error && error.message.includes('Missing required field')) {
      throw error; // Re-throw validation errors as-is
    }

    throw new Error(
      `Failed to parse AI mapping response: ${error instanceof Error ? error.message : 'Unknown error'}. Ensure response is valid JSON with detected_type and field_mappings.`,
    );
  }
};

/**
 * Get CSV field mapping with retry logic
 */
export const getCsvFieldMappingWithRetry = async (
  mappingPrompt: string,
  sessionId: string,
  maxRetries: number = 3,
): Promise<CsvFieldMapping> => {
  const allErrors: string[] = [];
  let lastError: string | undefined;

  appLogger.info(`Starting CSV field mapping with ${maxRetries} max retries`);

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      appLogger.info(`Attempt ${attempt} of ${maxRetries} for CSV field mapping`);

      // Build enhanced prompt with error context
      let promptToUse = mappingPrompt;

      // Add error context from previous attempts
      if (lastError) {
        promptToUse = `PREVIOUS ATTEMPT FAILED: ${lastError}

🚨 CRITICAL: Fix the error and respond with VALID JSON ONLY 🚨

${mappingPrompt}

COMMON FIXES:
- If you returned a UUID or random text: Return proper JSON object
- If JSON was malformed: Check brackets, quotes, and commas
- If missing fields: Include detected_type, field_mappings, confidence, reasoning
- Start with { and end with } - NO other text`;
      }

      // On the last attempt, use an even more explicit prompt
      if (attempt === maxRetries) {
        promptToUse = `🚨 FINAL ATTEMPT - RETURN ONLY VALID JSON 🚨

PREVIOUS ERRORS: ${lastError || 'Invalid responses'}

Copy this exact structure and modify the values:

{
  "detected_type": "person",
  "field_mappings": {
    "Account Name": "company_name",
    "Title": "job_title",
    "First Name": "first_name",
    "Last Name": "last_name",
    "Email": "email"
  },
  "confidence": 0.95,
  "reasoning": "Standard person contact data",
  "suggested_filename": "sales_contacts_2024.csv",
  "suggested_title": "Sales Team Contact List"
}

REPLACE field_mappings keys with actual CSV column names. Use ONLY these values: first_name, last_name, email, company_name, phone, job_title, website, linkedin_url

START WITH { and END WITH } - NO OTHER TEXT!`;
      }

      const result = await getCsvFieldMapping(promptToUse, sessionId, lastError, attempt);

      appLogger.info(`Successfully got CSV field mapping on attempt ${attempt}`);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      lastError = errorMessage;
      allErrors.push(`Attempt ${attempt}: ${errorMessage}`);

      // Check if this is a validation error (AI succeeded but returned invalid mapping)
      // These errors are permanent - retrying won't fix the AI's logical mistake since
      // the AI already generated a response, it's just logically invalid
      const isValidationError =
        errorMessage.includes('Duplicate mapping') ||
        errorMessage.includes('Invalid standard field') ||
        errorMessage.includes('Invalid detected_type') ||
        errorMessage.includes('Invalid field_mappings format') ||
        errorMessage.includes('Invalid confidence value') ||
        errorMessage.includes('Invalid mapping response format') ||
        errorMessage.includes('Missing required field') ||
        errorMessage.includes('field_mappings is empty');

      if (isValidationError) {
        appLogger.warn('Validation error detected - not retrying', {
          error: errorMessage,
          attempt,
        });
        // Throw immediately - retrying validation errors wastes API calls
        throw new Error(
          `AI mapping validation failed: ${errorMessage}. Please review and correct the mappings in the UI.`,
        );
      }

      appLogger.error(`Attempt ${attempt} failed:`, { data: errorMessage });

      // Log accumulated errors for context
      if (attempt > 1) {
        appLogger.info('Previous errors:', { data: allErrors.slice(0, -1) });
      }

      if (attempt === maxRetries) {
        // As a final fallback, analyze the prompt to create appropriate mapping
        appLogger.warn('Using fallback mapping due to repeated failures');

        // Extract CSV headers from the prompt (look for "CSV Headers:" line)
        const csvHeadersMatch = mappingPrompt.match(/CSV Headers:\s*([^\n]+)/);
        let columns: string[] = [];

        if (csvHeadersMatch) {
          // Parse actual CSV headers from the prompt
          columns = csvHeadersMatch[1].split(',').map((col) => col.trim());
        } else {
          // If no headers found, try to extract column names from the sample data JSON
          const jsonMatch = mappingPrompt.match(/Sample Data[^{]*(\{[\s\S]*?\})/);
          if (jsonMatch) {
            try {
              const sampleObj = JSON.parse(jsonMatch[1]);
              columns = Object.keys(sampleObj);
            } catch {
              // Fallback to empty columns if parsing fails
              columns = [];
            }
          }
        }

        // Create fallback mapping based on detected columns
        const fallbackMappings: Record<string, string> = {};

        columns.forEach((column) => {
          const lowerColumn = column.toLowerCase();
          if (lowerColumn.includes('first') && lowerColumn.includes('name')) {
            fallbackMappings[column] = 'first_name';
          } else if (lowerColumn.includes('last') && lowerColumn.includes('name')) {
            fallbackMappings[column] = 'last_name';
          } else if (lowerColumn.includes('email')) {
            fallbackMappings[column] = 'email';
          } else if (lowerColumn.includes('company') || lowerColumn.includes('account')) {
            fallbackMappings[column] = 'company_name';
          } else if (lowerColumn.includes('title') || lowerColumn.includes('job')) {
            fallbackMappings[column] = 'job_title';
          } else if (lowerColumn.includes('phone')) {
            fallbackMappings[column] = 'phone';
          }
        });

        return {
          detected_type: 'person',
          field_mappings:
            Object.keys(fallbackMappings).length > 0
              ? fallbackMappings
              : {
                  'Account Name': 'company_name',
                  'First Name': 'first_name',
                  'Last Name': 'last_name',
                  Email: 'email',
                  Title: 'job_title',
                },
          confidence: 0.8,
          reasoning: `Fallback mapping - AI failed ${maxRetries} times. Errors: ${allErrors.join('; ')}`,
          suggested_filename: 'contacts_import.csv',
          suggested_title: 'Imported Contact List',
        };
      }

      // Wait a bit before retrying
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  // This should never be reached due to the throw in the loop, but TypeScript needs it
  throw new Error('Unexpected error in getCsvFieldMappingWithRetry');
};
